// frontend/src/site/components/SiteListingCard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { storage } from '../../services/firebase';
import { FaBath, FaCar, FaBed, FaRuler, FaHome } from 'react-icons/fa';

import { useSite } from '../SiteContext';
import {
  cardClasses,
  buttonClasses,
  headingClasses,
  mutedTextClasses,
  chipClasses,
} from '../theme/siteStyles';

const cx = (...c) => c.filter(Boolean).join(' ');
const isHttpUrl = (u) => typeof u === 'string' && /^https?:\/\//i.test(u);

const firstFromArray = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  const first = arr[0];
  if (typeof first === 'string') return first;
  if (first && typeof first.url === 'string') return first.url;
  const found = arr.find((x) => typeof x === 'string' && isHttpUrl(x));
  return found || '';
};

// ✅ placeholder inline (não depende de arquivo externo)
const FALLBACK_IMG =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="900">
      <defs><linearGradient id="g" x1="0" x2="1"><stop stop-color="#0f172a"/><stop offset="1" stop-color="#111827"/></linearGradient></defs>
      <rect width="100%" height="100%" fill="url(#g)"/>
      <text x="50%" y="50%" font-family="system-ui, -apple-system, Segoe UI, Roboto" font-size="36" fill="#94a3b8" text-anchor="middle" dominant-baseline="middle">
        sem imagem
      </text>
    </svg>`
  );

const moneyBRL = (v) => {
  const n = Number(v || 0);
  if (!n) return 'R$ --';
  return `R$ ${n.toLocaleString('pt-BR')}`;
};

const m2 = (v) => {
  const n = Number(v);
  if (!n) return null;
  return `${n.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}m²`;
};

function densityCfg(theme) {
  const d = theme?.layoutDensity || 'normal';
  // ✅ mantém coerente com siteStyles.js (sem precisar importar função interna)
  if (d === 'compact') {
    return { pad: 'p-5', imgH: 'h-64', gap: 'gap-2.5' };
  }
  if (d === 'airy') {
    return { pad: 'p-7 md:p-8', imgH: 'h-96', gap: 'gap-3' };
  }
  return { pad: 'p-6', imgH: 'h-80', gap: 'gap-3' };
}

export default function SiteListingCard({ p, className, whatsappUrl }) {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { theme } = useSite() || {};

  const dens = useMemo(() => densityCfg(theme), [theme]);

  // ====== IMAGEM ======
  const [thumbUrl, setThumbUrl] = useState(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  const directHttp = useMemo(() => {
    const arrFirst = firstFromArray(p?.imagens) || firstFromArray(p?.images);
    return (
      (isHttpUrl(p?.thumb) && p?.thumb) ||
      (isHttpUrl(p?.thumbnail) && p?.thumbnail) ||
      (isHttpUrl(p?.capaUrl) && p?.capaUrl) ||
      (isHttpUrl(p?.cover) && p?.cover) ||
      (isHttpUrl(p?.image) && p?.image) ||
      (isHttpUrl(p?.img) && p?.img) ||
      (isHttpUrl(arrFirst) && arrFirst) ||
      ''
    );
  }, [p]);

  const storageCandidate = useMemo(() => {
    const arrFirst = firstFromArray(p?.imagens) || firstFromArray(p?.images);
    return (
      (!isHttpUrl(p?.thumb) && p?.thumb) ||
      (!isHttpUrl(p?.thumbnail) && p?.thumbnail) ||
      (!isHttpUrl(p?.capaUrl) && p?.capaUrl) ||
      (!isHttpUrl(arrFirst) && arrFirst) ||
      (!isHttpUrl(p?.cover) && p?.cover) ||
      (!isHttpUrl(p?.image) && p?.image) ||
      (!isHttpUrl(p?.img) && p?.img) ||
      ''
    );
  }, [p]);

  useEffect(() => {
    let alive = true;
    setImgLoaded(false);

    (async () => {
      if (directHttp) {
        alive && setThumbUrl(directHttp);
        return;
      }
      if (storageCandidate) {
        try {
          const url = await getDownloadURL(
            storageRef(storage, storageCandidate)
          );
          alive && setThumbUrl(url);
          return;
        } catch {
          // ignore
        }
      }
      alive && setThumbUrl(null);
    })();

    return () => {
      alive = false;
    };
  }, [directHttp, storageCandidate]);

  // ====== CAMPOS ======
  const preco = Number(p?.preco || 0);
  const areaTerreno = Number(p?.areaTerreno || 0);
  const areaConstruida = Number(p?.areaConstruida || 0);
  const dormitorios = Number(p?.dormitorios || 0);
  const banheiros = Number(p?.banheiros || 0);
  const vagas = Number(p?.vagas || 0);

  const tipo = p?.tipo || 'Imóvel';
  const refCodigo = p?.referencia || p?.refCodigo || '--';
  const titulo = p?.titulo || p?.bairro || 'Imóvel';
  const cidade = p?.cidade || '--';
  const bairro = p?.bairro || '';

  const aceitaPermuta =
    p?.aceitaPermuta === true ||
    p?.permuta === true ||
    p?.aceita_permuta === true;

  const isUpgrade =
    p?.upgrade === true || p?.trocaUpgrade === true || p?.isUpgrade === true;

  const imovelId = p?.imovelId || p?.id || p?.objectID;
  const href = `/u/${handle}/imovel/${imovelId}`;
  const wa = whatsappUrl || '';

  const goDetalhes = (e) => {
    e?.preventDefault?.();
    navigate(href);
  };

  const abrirWA = (e) => {
    e?.preventDefault?.();
    if (!wa) return;
    window.open(wa, '_blank', 'noopener,noreferrer');
  };

  const isDark = (theme?.mode || 'light') === 'dark';

  return (
    <div className={cx('group overflow-hidden', cardClasses(theme), className)}>
      {/* Imagem */}
      <div className="relative overflow-hidden">
        {!imgLoaded && (
          <div
            className="absolute inset-0 animate-pulse"
            style={{
              background: isDark
                ? 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.09), rgba(255,255,255,0.05))'
                : 'linear-gradient(135deg, rgba(15,23,42,0.05), rgba(15,23,42,0.09), rgba(15,23,42,0.05))',
            }}
          />
        )}

        <img
          src={thumbUrl || FALLBACK_IMG}
          alt={titulo}
          loading="lazy"
          decoding="async"
          className={cx(
            'w-full object-cover transition-transform duration-500',
            dens.imgH,
            'group-hover:scale-110',
            !imgLoaded && 'opacity-0'
          )}
          onLoad={() => setImgLoaded(true)}
          onError={(e) => {
            if (e.currentTarget.src !== FALLBACK_IMG) {
              e.currentTarget.src = FALLBACK_IMG;
            }
            setImgLoaded(true);
          }}
        />

        {/* Badges (sem cor fixa: usa tokens + variações neutras) */}
        <div className="absolute top-4 left-4 flex flex-col gap-2">
          {aceitaPermuta && (
            <span
              className={cx(chipClasses(theme), 'shadow-sm')}
              style={{
                backgroundColor: isDark
                  ? 'rgba(255,255,255,0.06)'
                  : 'rgba(15,23,42,0.03)',
                borderColor: 'var(--border)',
              }}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: 'var(--accent)' }}
              />
              Aceita Permuta
            </span>
          )}

          {isUpgrade && (
            <span
              className={cx(chipClasses(theme), 'shadow-sm')}
              style={{
                backgroundColor: isDark
                  ? 'rgba(255,255,255,0.06)'
                  : 'rgba(15,23,42,0.03)',
                borderColor: 'var(--border)',
              }}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: 'var(--accent)' }}
              />
              Upgrade
            </span>
          )}
        </div>
      </div>

      {/* Conteúdo */}
      <div className={dens.pad}>
        <div className="mb-3">
          <h3
            className={cx(headingClasses(theme, 'h4'), 'mb-0.5 line-clamp-1')}
          >
            {tipo}
          </h3>
          <p className={cx(mutedTextClasses(), 'text-sm')}>
            {bairro ? `${bairro}, ` : ''}
            {cidade}
          </p>
        </div>

        <div className={cx('grid grid-cols-2 mb-4 text-sm', dens.gap)}>
          <div className={cx('flex items-center gap-2', mutedTextClasses())}>
            <FaBed className="h-4 w-4 opacity-80" />
            <span>{dormitorios || 0} dormitórios</span>
          </div>
          <div className={cx('flex items-center gap-2', mutedTextClasses())}>
            <FaBath className="h-4 w-4 opacity-80" />
            <span>{banheiros || 0} banheiros</span>
          </div>
          <div className={cx('flex items-center gap-2', mutedTextClasses())}>
            <FaCar className="h-4 w-4 opacity-80" />
            <span>{vagas || 0} vagas</span>
          </div>
          <div className={cx('flex items-center gap-2', mutedTextClasses())}>
            <FaRuler className="h-4 w-4 opacity-80" />
            <span>{m2(areaTerreno) || '0m²'} terreno</span>
          </div>

          <div
            className={cx(
              'flex items-center gap-2',
              mutedTextClasses(),
              'col-span-2'
            )}
          >
            <FaHome className="h-4 w-4 opacity-80" />
            <span>{m2(areaConstruida) || '0m²'} construído</span>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <span className="text-2xl md:text-3xl font-bold text-[color:var(--text)]">
            {moneyBRL(preco)}
          </span>

          <span className={cx('text-xs', mutedTextClasses())}>
            Ref.: <b className="text-[color:var(--text)]">{refCodigo}</b>
          </span>
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={abrirWA}
            disabled={!wa}
            className={cx(
              buttonClasses(theme, 'primary', 'flex-1'),
              !wa && 'opacity-50 cursor-not-allowed'
            )}
            aria-label="Conversar no WhatsApp"
            title={wa ? 'Conversar' : 'WhatsApp não configurado'}
          >
            Conversar
          </button>

          <button
            type="button"
            onClick={goDetalhes}
            className={buttonClasses(theme, 'secondary', 'flex-1')}
            aria-label="Ver detalhes do imóvel"
          >
            Ver Detalhes
          </button>
        </div>
      </div>

      {imovelId && (
        <Link to={href} className="sr-only" aria-hidden="true">
          {titulo}
        </Link>
      )}
    </div>
  );
}
